import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Loading } from "@/components/ui/loading";
import React from "react";
import { BonusType } from "@/types/bonus";

export const bonusFormSchema = z
  .object({
    title: z.string().min(2, "Title must be at least 2 characters"),
    bonusType: z.nativeEnum(BonusType),
    startDate: z.date(),
    endDate: z.date(),
    requiredParties: z.coerce.number().min(1).optional().nullable(),
    dollarAmount: z.coerce.number().min(0).optional().nullable(),
    topN: z.coerce.number().min(1).optional().nullable(),
    notes: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.bonusType !== BonusType.INCENTIVE) {
      if (!data.requiredParties || data.requiredParties < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required parties must be at least 1",
          path: ["requiredParties"],
        });
      }
      if (data.dollarAmount == null || data.dollarAmount < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Bonus amount must be at least $1",
          path: ["dollarAmount"],
        });
      }
    }
    if (data.bonusType === BonusType.INCENTIVE) {
      if (!data.topN || data.topN < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Top N must be at least 1",
          path: ["topN"],
        });
      }
    }
  });

export type BonusFormValues = z.infer<typeof bonusFormSchema>;

interface BonusFormProps {
  initialValues: BonusFormValues;
  onSubmit: (values: BonusFormValues) => Promise<void>;
  loading: boolean;
  submitLabel: string;
  cancelLabel?: string;
  onCancel?: () => void;
}

export function BonusForm({
  initialValues,
  onSubmit,
  loading,
  submitLabel,
  cancelLabel,
  onCancel,
}: BonusFormProps) {
  const form = useForm<BonusFormValues>({
    resolver: zodResolver(bonusFormSchema),
    defaultValues: initialValues,
  });

  const bonusType = form.watch("bonusType");
  const isIncentive = bonusType === BonusType.INCENTIVE;

  React.useEffect(() => {
    form.reset(initialValues);
  }, [initialValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Program Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter program name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bonusType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bonus Type</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={BonusType.REFERRAL}>Referral</SelectItem>
                  <SelectItem value={BonusType.SEASONAL}>Seasonal</SelectItem>
                  <SelectItem value={BonusType.INCENTIVE}>Incentive</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Start Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value
                        ? format(field.value, "PPP")
                        : "Select start date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  side="bottom"
                  avoidCollisions={false}
                >
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>End Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value
                        ? format(field.value, "PPP")
                        : "Select end date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  side="bottom"
                  avoidCollisions={false}
                >
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) => date < form.getValues("startDate")}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isIncentive && (
          <>
            <FormField
              control={form.control}
              name="requiredParties"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Required Number of Parties</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? null : e.target.valueAsNumber,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dollarAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bonus Amount ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? null : e.target.valueAsNumber,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        {isIncentive && (
          <FormField
            control={form.control}
            name="topN"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Top N Performers to Reward</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : e.target.valueAsNumber,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional details visible to employees..."
                  rows={3}
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loading text="Submitting . . ." />
          </div>
        ) : (
          <div className="flex gap-4">
            <Button type="submit" className="flex-1">
              {submitLabel}
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1"
              >
                {cancelLabel || "Cancel"}
              </Button>
            )}
          </div>
        )}
      </form>
    </Form>
  );
}
